import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/**
 * Root-level service for drawer component action notifications.
 *
 * Provides a lightweight pub-sub mechanism for drawer-related UI events
 * (e.g., open, close, toggle) that need to coordinate between components
 * without going through NgRx.
 *
 * This complements (not replaces) NgRx actions - components should still
 * dispatch actions for state management, but can use this service for
 * direct component coordination.
 *
 * **Why This Service Exists**:
 * Components with component-level providers (e.g., provideComponentStore)
 * create isolated injector scopes that cannot access NgRx Store in
 * production builds due to tree-shaking. This service provides a pure
 * RxJS alternative that bypasses Angular DI hierarchy issues.
 *
 * **Usage Pattern**:
 * - Consumer Components: Call notify methods when dispatching drawer actions
 * - CdwDrawerComponent: Subscribes to observables filtered by drawerId
 *
 * **Multi-Instance Support**:
 * The drawerId allows multiple drawer instances on the same page to filter
 * notifications, ensuring each drawer only responds to its own events.
 *
 * @example
 * ```typescript
 * // In AccessManagementComponent:
 * this.appState.dispatch(openDrawer({ id: 'accessManagementDrawer' }));
 * this.drawerNotifications.notifyOpenDrawer('accessManagementDrawer');
 *
 * // In CdwDrawerComponent:
 * this.drawerNotifications.openDrawer$
 *   .pipe(
 *     filter(({ id }) => id === this.drawerId),
 *     takeUntil(this.destroy$)
 *   )
 *   .subscribe(() => {
 *     this.isDrawerOpenSubject.next(true);
 *   });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class DrawerNotificationService {
  /**
   * Private Subject for open drawer notifications.
   * Kept private to enforce one-way data flow through public methods.
   */
  private readonly openDrawerSubject = new Subject<{ id: string }>();

  /**
   * Private Subject for close drawer notifications.
   * Kept private to enforce one-way data flow through public methods.
   */
  private readonly closeDrawerSubject = new Subject<{ id: string }>();

  /**
   * Private Subject for toggle drawer notifications.
   * Kept private to enforce one-way data flow through public methods.
   */
  private readonly toggleDrawerSubject = new Subject<{ id: string }>();

  /**
   * Private Subject for add drawer notifications.
   * Kept private to enforce one-way data flow through public methods.
   */
  private readonly addDrawerSubject = new Subject<{
    id: string;
    isDrawerOpen: boolean;
  }>();

  /**
   * Private Subject for remove drawer notifications.
   * Kept private to enforce one-way data flow through public methods.
   */
  private readonly removeDrawerSubject = new Subject<{ id: string }>();

  /**
   * Private Subject for close all drawers notifications.
   * Kept private to enforce one-way data flow through public methods.
   */
  private readonly closeAllDrawersSubject = new Subject<void>();

  /**
   * Observable stream of open drawer notifications.
   *
   * Components should filter this stream by drawerId to handle only
   * their own events in multi-instance scenarios.
   *
   * **Important**: Always use takeUntil() to prevent memory leaks when
   * subscribing in component lifecycle methods.
   */
  public readonly openDrawer$: Observable<{ id: string }> =
    this.openDrawerSubject.asObservable();

  /**
   * Observable stream of close drawer notifications.
   *
   * Components should filter this stream by drawerId to handle only
   * their own events in multi-instance scenarios.
   *
   * **Important**: Always use takeUntil() to prevent memory leaks when
   * subscribing in component lifecycle methods.
   */
  public readonly closeDrawer$: Observable<{ id: string }> =
    this.closeDrawerSubject.asObservable();

  /**
   * Observable stream of toggle drawer notifications.
   *
   * Components should filter this stream by drawerId to handle only
   * their own events in multi-instance scenarios.
   *
   * **Important**: Always use takeUntil() to prevent memory leaks when
   * subscribing in component lifecycle methods.
   */
  public readonly toggleDrawer$: Observable<{ id: string }> =
    this.toggleDrawerSubject.asObservable();

  /**
   * Observable stream of add drawer notifications.
   *
   * Components should filter this stream by drawerId to handle only
   * their own events in multi-instance scenarios.
   *
   * **Important**: Always use takeUntil() to prevent memory leaks when
   * subscribing in component lifecycle methods.
   */
  public readonly addDrawer$: Observable<{
    id: string;
    isDrawerOpen: boolean;
  }> = this.addDrawerSubject.asObservable();

  /**
   * Observable stream of remove drawer notifications.
   *
   * Components should filter this stream by drawerId to handle only
   * their own events in multi-instance scenarios.
   *
   * **Important**: Always use takeUntil() to prevent memory leaks when
   * subscribing in component lifecycle methods.
   */
  public readonly removeDrawer$: Observable<{ id: string }> =
    this.removeDrawerSubject.asObservable();

  /**
   * Observable stream of close all drawers notifications.
   *
   * This notification is not filtered by drawerId - all drawers should
   * respond to this event.
   *
   * **Important**: Always use takeUntil() to prevent memory leaks when
   * subscribing in component lifecycle methods.
   */
  public readonly closeAllDrawers$: Observable<void> =
    this.closeAllDrawersSubject.asObservable();

  /**
   * Notify that a drawer should be opened.
   *
   * This method should be called by consumer components in parallel with
   * the NgRx openDrawer action dispatch to maintain both state management
   * and component coordination paths.
   *
   * **Best Practice**: Always call this in parallel with the NgRx
   * action dispatch to maintain both state management and component
   * coordination paths.
   *
   * @param id The drawer ID to open. Should match the drawerId property
   *           of the target CdwDrawerComponent.
   *
   * @example
   * ```typescript
   * // Dispatch both NgRx action and Subject notification:
   * this.store.dispatch(openDrawer({ id: 'myDrawer' }));
   * this.drawerNotifications.notifyOpenDrawer('myDrawer');
   * ```
   */
  public notifyOpenDrawer(id: string): void {
    this.openDrawerSubject.next({ id });
  }

  /**
   * Notify that a drawer should be closed.
   *
   * This method should be called by consumer components in parallel with
   * the NgRx closeDrawer action dispatch to maintain both state management
   * and component coordination paths.
   *
   * **Best Practice**: Always call this in parallel with the NgRx
   * action dispatch to maintain both state management and component
   * coordination paths.
   *
   * @param id The drawer ID to close. Should match the drawerId property
   *           of the target CdwDrawerComponent.
   *
   * @example
   * ```typescript
   * // Dispatch both NgRx action and Subject notification:
   * this.store.dispatch(closeDrawer({ id: 'myDrawer' }));
   * this.drawerNotifications.notifyCloseDrawer('myDrawer');
   * ```
   */
  public notifyCloseDrawer(id: string): void {
    this.closeDrawerSubject.next({ id });
  }

  /**
   * Notify that a drawer should toggle its open/close state.
   *
   * This method should be called by consumer components in parallel with
   * the NgRx toggleDrawer action dispatch to maintain both state management
   * and component coordination paths.
   *
   * **Best Practice**: Always call this in parallel with the NgRx
   * action dispatch to maintain both state management and component
   * coordination paths.
   *
   * @param id The drawer ID to toggle. Should match the drawerId property
   *           of the target CdwDrawerComponent.
   *
   * @example
   * ```typescript
   * // Dispatch both NgRx action and Subject notification:
   * this.store.dispatch(toggleDrawer({ id: 'myDrawer' }));
   * this.drawerNotifications.notifyToggleDrawer('myDrawer');
   * ```
   */
  public notifyToggleDrawer(id: string): void {
    this.toggleDrawerSubject.next({ id });
  }

  /**
   * Notify that a drawer is being added to the application.
   *
   * This method should be called by CdwDrawerComponent during ngOnInit
   * to register the drawer instance. In parallel, the component should
   * dispatch the addDrawer NgRx action if Store is available.
   *
   * @param id The drawer ID being added. Should match the drawerId property
   *           of the CdwDrawerComponent.
   * @param isDrawerOpen Initial open/close state of the drawer.
   *
   * @example
   * ```typescript
   * // In CdwDrawerComponent ngOnInit:
   * this.drawerNotifications.notifyAddDrawer(this.drawerId, false);
   * ```
   */
  public notifyAddDrawer(id: string, isDrawerOpen: boolean): void {
    this.addDrawerSubject.next({ id, isDrawerOpen });
  }

  /**
   * Notify that a drawer is being removed from the application.
   *
   * This method should be called by CdwDrawerComponent during ngOnDestroy
   * to unregister the drawer instance. In parallel, the component should
   * dispatch the removeDrawer NgRx action if Store is available.
   *
   * @param id The drawer ID being removed. Should match the drawerId property
   *           of the CdwDrawerComponent.
   *
   * @example
   * ```typescript
   * // In CdwDrawerComponent ngOnDestroy:
   * this.drawerNotifications.notifyRemoveDrawer(this.drawerId);
   * ```
   */
  public notifyRemoveDrawer(id: string): void {
    this.removeDrawerSubject.next({ id });
  }

  /**
   * Notify that all drawers should be closed.
   *
   * This method should be called by consumer components in parallel with
   * the NgRx closeAllDrawers action dispatch to maintain both state
   * management and component coordination paths.
   *
   * **Best Practice**: Always call this in parallel with the NgRx
   * action dispatch to maintain both state management and component
   * coordination paths.
   *
   * @example
   * ```typescript
   * // Dispatch both NgRx action and Subject notification:
   * this.store.dispatch(closeAllDrawers());
   * this.drawerNotifications.notifyCloseAllDrawers();
   * ```
   */
  public notifyCloseAllDrawers(): void {
    this.closeAllDrawersSubject.next();
  }
}
